import React, { useState, useMemo } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { getModule, LIKERT, computeScore } from "../data/assessments";
import { useAssessments } from "../context/AssessmentContext";

const AssessmentModule = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const mod = useMemo(() => getModule(slug), [slug]);
  const { saveResult } = useAssessments();

  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});

  if (!mod) return <Navigate to="/assessment" replace />;

  const dim = mod.dimensions[idx];
  const progress = ((idx + (answers[dim?.key] ? 1 : 0)) / mod.dimensions.length) * 100;

  const onPick = (val) => {
    const next = { ...answers, [dim.key]: val };
    setAnswers(next);
    setTimeout(() => {
      if (idx < mod.dimensions.length - 1) {
        setIdx(idx + 1);
      } else {
        const score = computeScore(next, mod.dimensions);
        saveResult(mod.slug, { answers: next, score });
        navigate(`/assessment/${mod.slug}/results`);
      }
    }, 250);
  };

  const onBack = () => idx > 0 && setIdx(idx - 1);

  return (
    <div data-testid="assessment-module-page" className="min-h-screen pt-28 pb-20 bg-gradient-to-b from-brand-green-50/50 to-white">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        {/* Top */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/assessment" data-testid="back-to-hub" className="inline-flex items-center gap-2 text-sm text-brand-slate hover:text-brand-green transition-colors">
            <ArrowLeft className="h-4 w-4" /> All assessments
          </Link>
          <div className="text-xs tracking-[0.2em] uppercase text-brand-slate">
            Question {idx + 1} <span className="text-brand-slate/50">of {mod.dimensions.length}</span>
          </div>
        </div>

        {/* Title */}
        <div className="mb-10">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-3">{mod.name}</div>
          <h1 className="font-serif text-3xl md:text-4xl text-brand-ink tracking-tight leading-tight">{mod.headline}</h1>
        </div>

        {/* Progress */}
        <div className="h-1.5 bg-black/5 rounded-full overflow-hidden mb-12">
          <div data-testid="assessment-progress" className="h-full bg-brand-green rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        {/* Question card */}
        <div data-testid="question-card" className="bg-white border border-black/5 shadow-glass rounded-sm p-8 md:p-12">
          <div className="text-xs tracking-[0.25em] uppercase text-brand-green mb-4 font-semibold">Dimension · {dim.label}</div>
          <h2 className="font-serif text-2xl md:text-4xl text-brand-ink tracking-tight leading-tight">{dim.question}</h2>

          {/* Likert scale */}
          <div className="mt-10 grid grid-cols-5 gap-2 md:gap-4">
            {LIKERT.map(l => {
              const selected = answers[dim.key] === l.value;
              return (
                <button
                  key={l.value}
                  data-testid={`likert-${l.value}`}
                  onClick={() => onPick(l.value)}
                  className={`group flex flex-col items-center gap-3 p-3 md:p-5 rounded-sm border-2 transition-all ${
                    selected
                      ? "border-brand-green bg-brand-green text-white"
                      : "border-black/10 hover:border-brand-green hover:bg-brand-green-50"
                  }`}
                >
                  <div className={`h-12 w-12 md:h-14 md:w-14 rounded-full flex items-center justify-center font-serif text-xl ${selected ? "bg-white text-brand-green" : "bg-brand-green-50 text-brand-green group-hover:bg-white"}`}>
                    {l.value}
                  </div>
                  <div className={`text-[10px] md:text-xs font-medium tracking-wide text-center leading-tight ${selected ? "text-white" : "text-brand-slate"}`}>
                    {l.label}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="mt-10 flex items-center justify-between">
            <button
              data-testid="question-prev"
              onClick={onBack}
              disabled={idx === 0}
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-slate hover:text-brand-green disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Previous
            </button>
            <div className="flex items-center gap-2 text-xs text-brand-slate">
              <CheckCircle2 className="h-3 w-3 text-brand-green" />
              {Object.keys(answers).length} of {mod.dimensions.length} answered
            </div>
            <button
              data-testid="question-next"
              onClick={() => answers[dim.key] && idx < mod.dimensions.length - 1 && setIdx(idx + 1)}
              disabled={!answers[dim.key] || idx === mod.dimensions.length - 1}
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-green hover:gap-3 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Dimension list (preview) */}
        <div className="mt-12 flex flex-wrap gap-2 justify-center">
          {mod.dimensions.map((d, i) => (
            <button
              key={d.key}
              data-testid={`dim-jump-${i}`}
              onClick={() => setIdx(i)}
              className={`px-3 py-1.5 text-[11px] tracking-wide rounded-full border transition-colors ${
                answers[d.key]
                  ? "bg-brand-green/10 border-brand-green/30 text-brand-green"
                  : i === idx
                    ? "bg-brand-ink text-white border-brand-ink"
                    : "bg-white border-black/10 text-brand-slate hover:border-brand-green"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AssessmentModule;
